import { clsx } from "clsx";
import { Clock } from "lucide-react";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import { IMask, IMaskInput } from "react-imask";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) =>
	String(i).padStart(2, "0"),
);

const TIME_BLOCKS = {
	HH: { mask: IMask.MaskedRange, from: 0, to: 23, maxLength: 2 },
	mm: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
};

const COMPLETE_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

interface TimePickerProps
	extends Omit<ComponentProps<"input">, "value" | "onChange"> {
	/** Wall-clock time as `HH:mm`, or an empty string when unset. */
	value: string;
	/** Fires with a complete `HH:mm` value, or an empty string when cleared. */
	onChange: (next: string) => void;
	modal?: boolean;
}

// TimePicker is the sibling of DatePicker: a masked `HH:mm` input (react-imask)
// with a popover offering hour and minute columns. Typing a complete time or
// picking both parts emits onChange; partial input never leaves the field.
export function TimePicker({
	className,
	disabled,
	id,
	modal,
	name,
	placeholder = "hh:mm",
	value,
	onChange,
	onBlur,
	...props
}: TimePickerProps) {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const [text, setText] = useState(value);
	const lastSyncedRef = useRef(value);

	useEffect(() => {
		if (value !== lastSyncedRef.current) {
			lastSyncedRef.current = value;
			setText(value);
		}
	}, [value]);

	const [hour = "", minute = ""] = COMPLETE_TIME.test(text)
		? text.split(":")
		: [];

	function emit(next: string) {
		lastSyncedRef.current = next;
		setText(next);
		onChange(next);
	}

	// react-imask also raises onAccept when the value prop is set (initial
	// mount, external sync, the echo of emit), so anything matching the last
	// synced value is a programmatic update rather than typing.
	function handleAccept(next: string) {
		setText(next);
		if (next === lastSyncedRef.current) return;
		if (!next) {
			emit("");
			return;
		}
		if (COMPLETE_TIME.test(next)) emit(next);
	}

	function pick(part: "hour" | "minute", selected: string) {
		const nextHour = part === "hour" ? selected : hour || "00";
		const nextMinute = part === "minute" ? selected : minute || "00";
		emit(`${nextHour}:${nextMinute}`);
		if (part === "minute") setOpen(false);
	}

	return (
		<Popover open={open} onOpenChange={setOpen} modal={modal}>
			<div
				ref={wrapperRef}
				data-slot="time-picker"
				data-disabled={disabled ? "true" : undefined}
				className={clsx("time-picker", className)}
				aria-invalid={props["aria-invalid"]}
				onFocusCapture={(e) => {
					if (e.target instanceof HTMLInputElement) setOpen(true);
				}}
				// A modal popover (inside a Dialog) ignores focus leaving it, so
				// tabbing to the next field would leave the columns hanging open.
				onBlurCapture={(e) => {
					const next = e.relatedTarget;
					if (
						next == null ||
						wrapperRef.current?.contains(next) ||
						contentRef.current?.contains(next)
					) {
						return;
					}
					setOpen(false);
				}}
			>
				<IMaskInput
					className="time-picker__input"
					disabled={disabled}
					id={id}
					name={name}
					value={text}
					mask="HH:mm"
					blocks={TIME_BLOCKS}
					inputMode="numeric"
					placeholder={placeholder}
					onAccept={(next: string) => handleAccept(next)}
					onBlur={onBlur}
				/>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="time-picker__trigger"
						disabled={disabled}
						aria-label="Escolher horário"
					>
						<Clock className="time-picker__icon" />
					</button>
				</PopoverTrigger>
			</div>
			<PopoverContent
				ref={contentRef}
				align="end"
				className="time-picker__popover"
				onOpenAutoFocus={(e) => e.preventDefault()}
				onInteractOutside={(e) => {
					const target = e.target as Node | null;
					if (target && wrapperRef.current?.contains(target)) {
						e.preventDefault();
					}
				}}
			>
				<div className="time-picker__columns">
					<TimeColumn
						label="Hora"
						options={HOURS}
						selected={hour}
						onPick={(option) => pick("hour", option)}
					/>
					<TimeColumn
						label="Minuto"
						options={MINUTES}
						selected={minute}
						onPick={(option) => pick("minute", option)}
					/>
				</div>
			</PopoverContent>
		</Popover>
	);
}

interface TimeColumnProps {
	label: string;
	options: string[];
	selected: string;
	onPick: (option: string) => void;
}

// TimeColumn mounts together with the popover content (the Radix portal
// renders one tick after `open` flips), so scrolling the selected option into
// view on mount is what makes the list open at the current value.
function TimeColumn({ label, options, selected, onPick }: TimeColumnProps) {
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		listRef.current
			?.querySelector<HTMLElement>('[aria-selected="true"]')
			?.scrollIntoView({ block: "center" });
	}, []);

	return (
		<div
			ref={listRef}
			className="time-picker__column"
			role="listbox"
			aria-label={label}
		>
			{options.map((option) => (
				<button
					key={option}
					type="button"
					role="option"
					aria-selected={option === selected}
					className="time-picker__option"
					onClick={() => onPick(option)}
				>
					{option}
				</button>
			))}
		</div>
	);
}
